import React, { useEffect, useState } from 'react';
import { useApp } from '@/contexts/AppContext';
import SeverityBadge from '@/components/apex/SeverityBadge';
import ActionMenu from '@/components/apex/ActionMenu';
import { motion } from 'framer-motion';
import { GitBranch, User, Calendar, Activity, ArrowRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import RecordDrawer from '@/components/apex/RecordDrawer';
import useListDrawer from '@/hooks/useListDrawer';

const FILTERS = [
  { key: 'pipeline_bypass', label: 'Pipeline bypass', count: 7 },
  { key: 'wrong_identity', label: 'Wrong identity', count: 3 },
  { key: 'approved_drifted', label: 'Approved but drifted', count: 4 },
  { key: 'wrong_environment', label: 'Wrong environment', count: 2 },
  { key: 'rollback_incomplete', label: 'Rollback incomplete', count: 2 },
  { key: 'never_deployed', label: 'Never deployed', count: 1 },
  { key: 'approved_correct', label: 'Approved & correct', count: 835 },
];

function timeSince(iso) {
  const ms = Date.now() - new Date(iso).getTime();
  const days = Math.floor(ms / 86400000);
  if (days < 1) return `${Math.max(1, Math.floor(ms / 3600000))}h`;
  if (days < 31) return `${days}d`;
  return `${Math.floor(days / 30)}mo`;
}

const CHIP_COLOR = {
  pipeline_bypass: 'bg-red-50 text-apex-critical border-red-200',
  wrong_identity: 'bg-red-50 text-apex-critical border-red-200',
  approved_drifted: 'bg-amber-50 text-apex-warning border-amber-200',
  wrong_environment: 'bg-amber-50 text-apex-warning border-amber-200',
  rollback_incomplete: 'bg-amber-50 text-apex-warning border-amber-200',
  never_deployed: 'bg-slate-100 text-apex-neutral border-slate-200',
  approved_correct: 'bg-emerald-50 text-apex-success border-emerald-200',
};

export default function ChangeLegitimacy() {
  const { seed } = useApp();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [filter, setFilter] = useState('pipeline_bypass');

  // Auto-switch to the classification that contains the deep-linked record (UX polish from iter 11 review)
  useEffect(() => {
    const id = params.get('open');
    if (!id || !seed) return;
    const target = seed.changes_list.find((c) => c.id === id);
    if (target && target.classification !== filter) setFilter(target.classification);
  }, [params, seed, filter]);

  const filtered = seed?.changes_list.filter((c) => c.classification === filter) ?? [];
  const { active, setActive, openPrev, openNext, index, total } = useListDrawer({ items: filtered });
  if (!seed) return null;

  const buildRecord = (c) => {
    const isApproved = filter === 'approved_correct';
    const verdictTone = isApproved ? 'positive' : (filter === 'pipeline_bypass' || filter === 'wrong_identity') ? 'critical' : 'warning';
    const filterLabel = FILTERS.find((f) => f.key === filter)?.label ?? '';
    const verdictLabels = {
      pipeline_bypass: 'Out-of-pipeline change',
      wrong_identity: 'Identity outside approval set',
      approved_drifted: 'Drifted from approval',
      wrong_environment: 'Routed to wrong environment',
      rollback_incomplete: 'Rollback never finished',
      never_deployed: 'Approved, never deployed',
      approved_correct: 'Clean change of custody',
    };
    const verdictSubs = {
      pipeline_bypass: `Applied directly via ${c.channel}. No pull request, no automated baseline check, no peer review.`,
      wrong_identity: `${c.actor} is outside the approved identity set for this resource class — change of custody is broken.`,
      approved_drifted: 'Approved at submission time, but the deployed state has moved away from the approved blueprint.',
      wrong_environment: 'The change matches an approval — but it landed in an environment that approval never authorized.',
      rollback_incomplete: 'A rollback was started but never produced the matching revert event in CloudTrail.',
      never_deployed: 'Change was approved but no corresponding deployment ever occurred in this environment.',
      approved_correct: 'Pull request, reviewer, pipeline, and runtime evidence all align with the approved baseline.',
    };
    const overSla = 0; // changes don't have explicit SLA
    return {
      idTag: c.id,
      title: c.title,
      severity: isApproved ? 'enforced' : (filter === 'pipeline_bypass' || filter === 'wrong_identity') ? 'critical' : 'high',
      subtitle: `${filterLabel} · classified by APEX from CloudTrail + Identity + Baseline state`,
      verdict: {
        tone: verdictTone,
        label: verdictLabels[filter] ?? filterLabel,
        sub: verdictSubs[filter] ?? 'Classified automatically based on actor, channel, and approved baseline.',
      },
      signal: isApproved ? null : {
        tone: verdictTone,
        lead: 'Audit translation:',
        body: 'Auditors won\'t accept a production change that bypasses the documented control surface. Until the path is corrected — or an explicit Risk Acceptance is filed — this counts toward the SOC 2 CC8.1 / ISO 27001 A.12.1.2 control gap.',
      },
      meta: [
        { label: 'Channel used', value: c.channel, sub: c.channel === 'AWS Console' ? 'Direct console action — evidence light' : 'Reviewable channel', tone: c.channel === 'AWS Console' ? 'critical' : '' },
        { label: 'Acting identity', value: c.actor, mono: true, sub: 'Resolved from CloudTrail userIdentity' },
        { label: 'When', value: new Date(c.when).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }), sub: 'Recorded in CloudTrail' },
        { label: 'Classification', value: filterLabel, tone: isApproved ? 'success' : 'critical', sub: isApproved ? 'Under continuous evidence' : 'Counts toward governance debt' },
      ],
      sections: [
        ...(c.expected_path && c.actual_path ? [{
          title: 'Path', icon: GitBranch,
          type: 'kv',
          items: [
            { k: 'Expected', v: c.expected_path, tone: 'success' },
            { k: 'Actual',   v: c.actual_path, tone: 'critical' },
          ],
        }] : []),
        {
          title: 'Change classification mix · last 30 days',
          type: 'chart',
          chart: {
            kind: 'radial',
            label: 'Where every production change landed',
            sublabel: 'APEX classified each event from CloudTrail + identity context',
            total: 854,
            segments: [
              { label: 'Approved & correct', value: 835, color: '#10B981' },
              { label: 'Pipeline bypass', value: 7, color: '#DC2626' },
              { label: 'Wrong identity', value: 3, color: '#B91C1C' },
              { label: 'Approved · drifted', value: 4, color: '#F59E0B' },
              { label: 'Wrong environment', value: 2, color: '#D97706' },
              { label: 'Rollback incomplete', value: 2, color: '#EAB308' },
              { label: 'Approved · never deployed', value: 1, color: '#94A3B8' },
            ],
          },
        },
        {
          title: 'Operational signals',
          type: 'kpis',
          kpis: [
            { label: 'Review trail', value: isApproved ? 'Complete' : 'Missing', sub: isApproved ? 'PR + reviewer attached' : 'No PR, no reviewer captured', tone: isApproved ? 'success' : 'critical', delta: isApproved ? 'flat' : 'down' },
            { label: 'Time-since change', value: timeSince(c.when), sub: 'Recency in CloudTrail history', tone: 'lens', delta: 'flat' },
            { label: 'Audit weight', value: isApproved ? 'Routine' : 'Material', sub: isApproved ? 'Standard quarterly evidence' : 'Will surface in next audit pull', tone: isApproved ? 'success' : 'warning', delta: isApproved ? 'flat' : 'up' },
          ],
        },
        {
          title: 'Audit translation',
          type: 'list',
          items: [
            'SOC 2 CC8.1 (change management) — failing this finding contributes to the open control gap.',
            'ISO 27001 A.12.1.2 (change control) — same impact, same packet line.',
            'Auditors will request the reviewed PR or change ticket. If absent, this becomes a documented exception.',
          ],
        },
        ...(filter !== 'approved_correct' ? [{
          title: 'Remediation steps',
          type: 'list',
          items: [
            'Document this change as an exception in the audit register with this Evidence ID.',
            `Verify whether ${c.actor} had an open change ticket for this resource at ${new Date(c.when).toLocaleDateString()}.`,
            'If no ticket exists, open a retrospective change request and attach this evidence packet.',
            'Implement a preventive control: require ticket reference in all console session tags.',
            'Schedule a review of all changes by this identity in the last 30 days.',
          ],
        }, {
          title: 'Governance decision record',
          type: 'kv',
          items: (() => {
            const decisions = {
              pipeline_bypass: [
                { k: 'Decision required', v: 'No exception record exists for this change', tone: 'critical' },
                { k: 'Expected approver', v: 'Change Advisory Board · stratum-payments-prod', tone: 'neutral' },
                { k: 'Constraint violated', v: 'CT-005 · Pipeline-bypass prohibition', tone: 'critical' },
                { k: 'Decision status', v: 'UNDOCUMENTED — governance debt accruing', tone: 'critical' },
              ],
              wrong_identity: [
                { k: 'Executing identity', v: c.actor, tone: 'critical' },
                { k: 'Approved identity', v: 'github-actions-deploy-role', tone: 'neutral' },
                { k: 'Prior exception', v: 'None found in apex-decisions', tone: 'critical' },
                { k: 'Decision status', v: 'UNDOCUMENTED — no authorization record', tone: 'critical' },
              ],
              approved_drifted: [
                { k: 'Original approval', v: 'CHG-7812 · Mar 14, 2026', tone: 'neutral' },
                { k: 'Approved state', v: 'Matches plan', tone: 'neutral' },
                { k: 'Observed deviation', v: 'Runtime state diverged post-deployment', tone: 'warning' },
                { k: 'Decision status', v: 'STALE — approved state no longer matches', tone: 'warning' },
              ],
            };
            return decisions[c.classification] ?? [
              { k: 'Decision record', v: 'No governance decision record found', tone: 'warning' },
            ];
          })(),
        }] : [{
          title: 'Governance decision record',
          type: 'kv',
          items: [
            { k: 'Change ticket', v: `CHG-${(c.id ?? '').toString().padStart(4, '0').slice(-4) || '7812'} · Approved`, tone: 'success' },
            { k: 'Approving authority', v: 'Change Advisory Board', tone: 'success' },
            { k: 'Pipeline identity', v: 'github-actions-deploy-role', tone: 'success' },
            { k: 'Decision status', v: 'VALID · Justification current', tone: 'success' },
          ],
        }]),
      ],
      actions: [
        { label: 'Open Change Calendar', onClick: () => navigate('/lens/changes'), primary: true },
        { label: 'Open Remediation', onClick: () => navigate('/lens/remediation') },
      ],
    };
  };

  return (
    <div className="space-y-5" data-testid="change-legitimacy">
      <div>
        <span className="text-[10px] uppercase tracking-[0.18em] text-apex-lens font-semibold">Evidence Engine · Change Legitimacy</span>
        <h1 className="text-2xl font-bold tracking-tight mt-1">Every change. Every actor. Every path.</h1>
        <p className="text-sm text-apex-neutral mt-1">{seed.changes_summary.total} changes · {seed.changes_summary.window_days} days · {seed.changes_summary.pipeline_bypass} pipeline bypasses · {seed.changes_summary.wrong_identity} wrong identity · {seed.changes_summary.approved_correct} approved and correct</p>
      </div>

      {/* Filter chips */}
      <div className="flex flex-wrap gap-2" data-testid="changes-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`filter-${f.key}`}
            className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-all ${filter === f.key ? CHIP_COLOR[f.key] : 'bg-white text-apex-neutral border-slate-200 hover:border-slate-300'}`}
          >
            {f.label} <span className="font-mono ml-1">{f.count}</span>
          </button>
        ))}
      </div>

      {/* List */}
      <div className="space-y-3">
        {filtered.map((c, i) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            data-testid={`change-${c.id}`}
            onClick={() => setActive(c)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive(c); } }}
            className="bg-white rounded-lg border border-slate-200 p-4 sm:p-5 cursor-pointer hover:shadow-sm hover:-translate-y-0.5 transition-all"
          >
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] font-mono text-apex-neutral">{c.id}</span>
                  <SeverityBadge severity={filter === 'approved_correct' ? 'enforced' : 'high'} size="sm" />
                </div>
                <h3 className="text-sm font-semibold text-apex-ink leading-snug">{c.title}</h3>
              </div>
              <div onClick={(e) => e.stopPropagation()}>
                <ActionMenu context={c.id} testId={`change-actions-${c.id}`} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[11px] mb-4">
              <Meta icon={Activity} label="Channel" value={c.channel} />
              <Meta icon={User} label="Actor" value={c.actor} mono />
              <Meta icon={Calendar} label="When" value={new Date(c.when).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' })} />
            </div>

            {filter === 'pipeline_bypass' && c.expected_path && (
              <div className="border-t border-dashed border-slate-200 pt-3 space-y-2 text-[11px] font-mono">
                <Path label="Expected path" value={c.expected_path} ok />
                <Path label="Actual path" value={c.actual_path} bad />
              </div>
            )}

            <div className="mt-3 pt-3 border-t border-slate-200 text-[11px] text-apex-lens font-semibold inline-flex items-center gap-1">
              Open detail <ArrowRight className="w-3 h-3" />
            </div>
          </motion.div>
        ))}
        {filtered.length === 0 && (
          <div className="bg-white rounded-lg border border-dashed border-slate-300 p-8 text-center text-sm text-apex-neutral">
            <GitBranch className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            No changes match this classification in the current window.
          </div>
        )}
      </div>

      <RecordDrawer
        open={!!active}
        onOpenChange={(o) => !o && setActive(null)}
        record={active ? buildRecord(active) : null}
        testId="change-drawer"
        onPrev={openPrev}
        onNext={openNext}
        position={index}
        total={total}
      />
    </div>
  );
}

function Meta({ icon: Icon, label, value, mono = false }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-apex-neutral font-semibold mb-0.5">
        <Icon className="w-3 h-3" />
        {label}
      </div>
      <div className={`text-[12px] text-apex-ink-2 ${mono ? 'font-mono' : ''}`}>{value}</div>
    </div>
  );
}

function Path({ label, value, ok = false, bad = false }) {
  return (
    <div className="flex items-start gap-2">
      <span className={`text-[10px] uppercase tracking-wider font-semibold shrink-0 w-24 ${ok ? 'text-apex-success' : bad ? 'text-apex-critical' : 'text-apex-neutral'}`}>{label}</span>
      <span className={`flex-1 ${bad ? 'text-apex-critical' : ok ? 'text-apex-success' : 'text-apex-ink-2'}`}>{value}</span>
    </div>
  );
}
